'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { HomeLogoLink } from '@/components/home-logo-link';
import { NavbarLogoImages } from '@/components/navbar/logo-images';
import { ArrowUpRight, Menu } from 'lucide-react';
import { ease } from '@/components/shared/motion';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import { legalNavHref } from '@/components/navbar/legal-nav-href';
import { NavbarMobileMenuContent } from '@/components/navbar/mobile-menu';
import { Drawer, DrawerTrigger } from '@/components/ui/drawer';
import { buildCompanyContactRows } from '@/sanity/lib/company-contact-rows';
import { trackCtaClick } from '@/lib/analytics/track-cta-click';
import type { MobileMenuContent, NavbarContent } from '@/sanity/lib/navigation';
import type { SiteSettingsContent } from '@/sanity/lib/site-settings';
import { usePathname } from 'next/navigation';

const navRevealVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.12,
      staggerChildren: 0.08,
    },
  },
};

const navItemVariants = {
  hidden: { opacity: 0, x: -18 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.65, ease },
  },
};

export default function LegalNav({
  content,
  mobileMenu,
  company,
}: {
  content?: NavbarContent | null;
  mobileMenu?: MobileMenuContent | null;
  company: SiteSettingsContent;
}) {
  const pathname = usePathname();
  const isHome = pathname === '/';
  const isCompactTouch = useIsCompactTouch();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasScrolledPastCompactPoint, setHasScrolledPastCompactPoint] = useState(false);
  const navLinks = content?.showLinks === false ? [] : (content?.links ?? []);
  const navCta = content?.showCTA === false ? null : content?.cta;
  const mobileLinks = mobileMenu?.showLinks === false ? [] : (mobileMenu?.links ?? navLinks);
  const mobileCta = mobileMenu?.showCTA === false ? null : (mobileMenu?.cta ?? navCta);
  const compactHeaderCta = (content?.showCTA === false ? null : navCta) ?? mobileCta;
  const mobileContactRows = useMemo(
    () =>
      mobileMenu?.showContactMethods === false
        ? []
        : buildCompanyContactRows(company, mobileMenu?.companyContactMethods ?? undefined),
    [company, mobileMenu?.showContactMethods, mobileMenu?.companyContactMethods],
  );
  const showMobileMenu =
    content?.showMobileMenu !== false &&
    mobileMenu?.show !== false &&
    Boolean(mobileLinks.length || mobileCta || mobileContactRows.length);
  const brandName = content?.companyName ?? content?.logos?.logo?.alt ?? 'Land Corp Group';
  const activeNavRevealVariants = useMemo(
    () =>
      isCompactTouch
        ? { hidden: {}, visible: { transition: { delayChildren: 0.18, staggerChildren: 0.04 } } }
        : navRevealVariants,
    [isCompactTouch],
  );
  const activeLogoVariants = useMemo(
    () =>
      isCompactTouch
        ? {
            hidden: { opacity: 0, x: -6 },
            visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease } },
          }
        : navItemVariants,
    [isCompactTouch],
  );
  const activeCtaVariants = useMemo(
    () =>
      isCompactTouch
        ? {
            hidden: { opacity: 0, x: 6 },
            visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease } },
          }
        : navItemVariants,
    [isCompactTouch],
  );

  useEffect(() => {
    const updateScrolledState = () => {
      setHasScrolled(window.scrollY > 4);
      setHasScrolledPastCompactPoint(window.scrollY >= 60);
    };

    updateScrolledState();
    window.addEventListener('scroll', updateScrolledState, { passive: true });
    window.addEventListener('resize', updateScrolledState);

    return () => {
      window.removeEventListener('scroll', updateScrolledState);
      window.removeEventListener('resize', updateScrolledState);
    };
  }, []);

  const menuDrawerDirection = isCompactTouch ? 'bottom' : 'right';

  return (
    <nav
      aria-label="On this page"
      className={cn(
        'inset-x-0 top-0 z-50 border-b transition-colors duration-300 bg-background',
        content?.sticky === false ? 'absolute' : 'fixed',
      )}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={activeNavRevealVariants}
        className={cn(
          'mx-auto flex max-w-7xl items-center gap-2 px-4 transition-[height] duration-300 ease-in-out max-[427px]:gap-1 max-[427px]:px-3 sm:px-6 lg:gap-4 min-[1324px]:px-0 lg:px-8',
          hasScrolledPastCompactPoint ? 'h-14' : 'h-14 xl:h-20',
        )}
      >
        {content?.showLogo !== false ? (
          <motion.div
            variants={activeLogoVariants}
            className="min-w-0 shrink max-[427px]:max-w-[48%]"
          >
            <HomeLogoLink href="/" aria-label={`${brandName} home`} className="block max-w-full">
              <NavbarLogoImages logos={content?.logos} alt={brandName} onSolidBackground />
            </HomeLogoLink>
          </motion.div>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-visible lg:gap-2 max-xl:hidden">
          {navLinks.map((link) => (
            <motion.div key={link.href} variants={navItemVariants} className="shrink-0">
              <Link
                href={legalNavHref(link.href)}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors sm:text-[11px] cursor-pointer',
                  hasScrolled
                    ? 'text-foreground hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-foreground border border-transparent hover:border-border transition-all duration-300 ease-in-out'
                    : 'text-foreground hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-foreground border border-transparent hover:border-border transition-all duration-300 ease-in-out',
                )}
                onClick={() =>
                  trackCtaClick({
                    location: 'legal_navbar_link',
                    label: link.label,
                    href: legalNavHref(link.href),
                  })
                }
              >
                {link.label}
              </Link>
            </motion.div>
          ))}
        </div>
        <motion.div variants={activeCtaVariants} className="ml-auto flex items-center gap-2">
          {navCta ? (
            <Link
              href={legalNavHref(navCta.href)}
              className="max-xl:hidden"
              data-lead-form-trigger={navCta.isLeadForm ? 'true' : undefined}
              data-tracking-location="legal_navbar_cta"
              onClick={() =>
                trackCtaClick({
                  location: 'legal_navbar_cta',
                  label: navCta.label,
                  href: legalNavHref(navCta.href),
                  isLeadForm: navCta.isLeadForm,
                  variant: 'primary',
                })
              }
            >
              <Button
                variant="outline"
                size="lg"
                className="group cursor-pointer relative inline-flex items-center gap-1.5 overflow-hidden rounded-full px-4 h-9 text-xs font-bold transition-all duration-300 ease-in-out focus-visible:outline-none uppercase tracking-wider border border-transparent bg-[#948B7F] text-white hover:bg-[#BFB7A1] hover:text-white"
              >
                <span>{navCta.label}</span>
                <ArrowUpRight className="size-4 shrink-0 transition-all duration-300 ease-in-out group-hover:rotate-45" />
              </Button>
            </Link>
          ) : null}
          {compactHeaderCta ? (
            <Link
              href={legalNavHref(compactHeaderCta.href)}
              className="xl:hidden"
              data-lead-form-trigger={compactHeaderCta.isLeadForm ? 'true' : undefined}
              data-tracking-location="legal_navbar_compact_cta"
              onClick={() =>
                trackCtaClick({
                  location: 'legal_navbar_compact_cta',
                  label: compactHeaderCta.label,
                  href: legalNavHref(compactHeaderCta.href),
                  isLeadForm: compactHeaderCta.isLeadForm,
                  variant: 'primary',
                })
              }
            >
              <Button
                variant="outline"
                size="lg"
                className="group cursor-pointer relative inline-flex shrink-0 items-center gap-1 overflow-hidden rounded-full px-3 min-h-11 h-11 text-[10px] font-bold transition-all duration-300 ease-in-out focus-visible:outline-none uppercase tracking-wider border sm:gap-1.5 sm:px-4 sm:text-xs border-transparent bg-[#948B7F] text-white hover:bg-[#BFB7A1] hover:text-white"
              >
                <span className="truncate">{compactHeaderCta.label}</span>
                <ArrowUpRight className="size-3.5 shrink-0 transition-all duration-300 ease-in-out group-hover:rotate-45 sm:size-4" />
              </Button>
            </Link>
          ) : null}
          {showMobileMenu ? (
            <Drawer direction={menuDrawerDirection}>
              <DrawerTrigger asChild>
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  aria-label="Open menu"
                  className={cn(
                    'xl:hidden shrink-0 rounded-full transition-all duration-300 ease-in-out cursor-pointer size-11 min-h-11 min-w-11 bg-[#948B7F] text-white hover:bg-[#BFB7A1]',
                  )}
                >
                  <Menu className="size-4" />
                </Button>
              </DrawerTrigger>
              <NavbarMobileMenuContent
                direction={menuDrawerDirection}
                showHomeLink={!isHome}
                linkResolution="legal"
                isHome={isHome}
                links={mobileLinks}
                mobileContactRows={mobileContactRows}
                mobileCta={mobileCta}
              />
            </Drawer>
          ) : null}
        </motion.div>
      </motion.div>
    </nav>
  );
}
